<template>
  <layout>
    <template v-slot:header>
      <AppHead title="MTD Shop" />
      <div class="admin-header-container">
        <h2 class="mr-3">MTD Shop - {{ mtd.company_name }}</h2>
      </div>
    </template>

    <template v-slot:breadcrumbs>
      <BreadCrumbs :activeItem="`MTD Shop - ${mtd.company_name}`" :crumbs="{ home: 'Dashboard' }"/>
    </template>

    <template v-slot:default>
      <div class="row px-3">
        <div class="col-12 d-flex flex-column flex-md-row mb-3">
          <p class="mr-1 font-weight-bold">Company</p> 
          <p class="mr-3 text-secondary"><inertia-link :href="route('mtd.view', mtd.id)">{{ mtd.company_name }}</inertia-link></p>
          <p class="mr-3 mb-0 text-secondary"><i class="fas fa-user mr-1"></i>{{ mtd.name }}</p>
          <p class="mr-3 mb-0 text-secondary"><i class="fas fa-envelope mr-1"></i><Email v-model="mtd.email" /></p>
          <p class="mb-0 text-secondary"><i class="fas fa-phone mr-1"></i><Phone v-model="mtd.phone" /></p>
        </div>

        <section class="col-xl-8 order-1 order-xl-0">
          <card>
            <div class="row">
              <div class="col-12 col-xl-4 mb-3 mb-xl-0">
                <jet-label for="manufacturer_select" value="Manufacturer:" />
                <Multiselect
                  v-model="manufacturerSelected"
                  :options="manufacturers"
                  :searchable="true"
                  class="admin-multiselect"
                  id="manufacturer_select"
                />
              </div>

              <div class="col-12 col-xl-4 mb-3 mb-xl-0">
                <jet-label for="category_select" value="Category:" />
                <Multiselect
                  v-model="categoriesSelected"
                  mode="tags"
                  :options="categories"
                  :searchable="true"
                  class="admin-multiselect"
                  id="category_select"
                />
              </div>

              <div class="col-12 col-xl-4">
                <jet-label for="search" value="Search:" />
                <input type="text" id="search" v-model="searchText" class="form-control">
              </div>

              <div class="col-12 mt-3 d-flex">
                <div class="mr-3 d-flex align-items-center">
                  <label for="active" class="mr-2 mb-0">Active Only:</label>
                  <input type="checkbox" id="active" v-model="searchActive">
                </div>

                <div class="d-flex align-items-center">
                  <label for="active" class="mr-2 mb-0">In Stock Only:</label>
                  <input type="checkbox" id="in_stock" v-model="searchInStock">
                </div>
              </div>
            </div>
          </card>

          <card class="mt-4 table-responsive">
            <DataTable 
              ref="admin-create-mtd-order"
              :value="products"
              :paginator="true"
              :lazy="true"
              :first="(pagination.page - 1) * pagination.per_page"
              :rows="pagination.per_page"
              :totalRecords="pagination.total"
              @page="onPage($event)"
              :sortField="sortField"
              :sortOrder="sortOrder"
              @sort="onSort"
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              paginatorPosition="both"
              :rowsPerPageOptions="[10, 20, 50, 100]"
              class="p-datatable-sm admin-datatable"
              :class="{'hide-select-all': products.length > 10}"
              id="shop-as-mtd-table"
              dataKey="id"
              v-model:selection="selectedProducts">
              <template #paginatorLeft>
                {{ products.length }} Products
              </template>
              <Column class="selection-mode-box" selectionMode="multiple" headerStyle="width: 3em"></Column>
              <Column class="pl-1" field="image_url">
                <template #body="slotProps">
                  <div class="d-flex w-100">
                    <i v-if="slotProps.data.image_url" @click="e => toggleOverlay(e, slotProps.data.id)" title="View product image" class="fas fa-image image-icon text-dark cursor-pointer h-100 fa-2x"></i>
                    <OverlayPanel v-if="slotProps.data.image_url" :ref="`product-list-image-overlay${slotProps.data.id}`" :showCloseIcon="true" :dismissable="true">
                      <img :src="slotProps.data.image_url" />
                    </OverlayPanel>
                  </div>
                </template>
              </Column>
              <Column field="name" header="Name" :sortable="true">
                <template #body="slotProps">
                  {{ slotProps.data.name }}
                  <span v-if="!slotProps.data.is_active" class="badge badge-danger">Inactive</span>
                </template>
              </Column>
              <Column field="inventoryInfo" header="Inventory" :sortable="true" sortField="inventoryInfo.current_inventory">
                <template #body="slotProps">
                  <InventoryInfo :inventoryInfo="slotProps.data.inventoryInfo" />
                </template>
              </Column>
              <Column field="sku" header="SKU" :sortable="true"></Column>
              <Column field="price_wholesale" header="Price (Wholesale)" :sortable="true">
                <template #body="slotProps">
                  {{ formatDollars(slotProps.data.price_wholesale) }}
                </template>
              </Column>
              <Column field="updated_at" header="Last Updated" :sortable="true">
                <template #body="slotProps">
                  {{ moment(slotProps.data.updated_at).format('M/D/YY h:mm A') }}
                </template>
              </Column>
            </DataTable>
          </card>
        </section>

        <section class="col-xl-4 order-0 order-xl-1 mb-3 mb-xl-0">
          <Cart :mtdId="mtd.id"
                :lineItemDescriptions="line_item_descriptions"
                :selectedProducts="selectedProducts"
                @updateSelectedProducts="(cartProductIds) => this.defaultSelectedProductIds = cartProductIds"></Cart>
        </section>
      </div>
    </template>
  </layout>
</template>

<script>
import Cart from '@/Components/Admin/Cart'
import JetLabel from '@/Jetstream/Label'
import Multiselect from '@vueform/multiselect'
import AppHead from "@/Shared/AppHead";
import Layout from '@/Shared/Admin/Layout'
import FlashMessages from '@/Shared/FlashMessages'
import moment from 'moment'
import BreadCrumbs from '@/Shared/BreadCrumbs'
import JetButton from '@/Jetstream/Button'
import Datatable from 'primevue/datatable'
import OverlayPanel from 'primevue/overlaypanel'
import InventoryInfo from '@/Components/General/InventoryInfo'

export default {
  components: {
    AppHead,
    Cart,
    JetLabel,
    Multiselect,
    JetButton,
    FlashMessages,
    Layout,
    BreadCrumbs,
    Datatable,
    OverlayPanel,
    InventoryInfo,
  },
  props: {
    mtd: Object,
    manufacturers: Array,
    categories: Array,
    line_item_descriptions: Array,
  },
  data() {
    return {
      sortField: null, // current sort field
      sortOrder: null, // current sort order
      pagination: {
        total: 0,
        per_page: 100,
        page: 1,
      },
      manufacturerSelected: null,
      categoriesSelected: null,
      searchText: '',
      searchActive: true,
      searchInStock: false,
      products: [],
      defaultSelectedProductIds: null,
      selectedProducts: [],
    }
  },
  mounted() {
    this.runSearch();
  },
  created() {
    this.moment = moment
  },
  watch: {
    manufacturerSelected() {
      this.runSearch();
    },
    categoriesSelected() {
      this.runSearch();
    },
    searchText() {
      this.runSearch();
    },
    searchActive() {
      this.runSearch();
    },
    searchInStock() {
      this.runSearch();
    },
    defaultSelectedProductIds() {
      this.syncCheckedProducts();
    }
  },
  methods: {
    onPage(e) {
      this.pagination.page = e.page + 1
      this.pagination.per_page = e.rows
      this.runSearch()
    },
    onSort(events) {
      this.sortField = events.sortField;
      this.sortOrder = events.sortOrder;
      this.runSearch();
    },
    runSearch() {
      let data = {
        sort_field: this.sortField,
        sort_order: this.sortOrder,
        query: this.searchText,
        categories: this.categoriesSelected,
        manufacturer_id: this.manufacturerSelected,
        include_inventory: 1,
        filter_instock: this.searchInStock,
        user_id: this.mtd.id,
      };

      if (this.searchActive) {
        data.filter_active = this.searchActive;
      }

      axios
        .post(route('products.search'), data, {
          params: {
            page: this.pagination.page, per_page: this.pagination.per_page
          }
        })
        .then((response) => {
          if (response.data.success) {
            this.products = response.data.data;
            this.pagination.total = response.data.total
            this.syncCheckedProducts();
          }
        });
    },
    toggleOverlay(event, id) {
      this.$refs[`product-list-image-overlay${id}`].toggle(event)
    },
    syncCheckedProducts() {
      this.selectedProducts = this.products.filter((product) => this.defaultSelectedProductIds.includes(product.id));
    }
  },
}

</script>

<style src="@vueform/multiselect/themes/default.css"></style>
